import { gql, Resolver } from '@apollo/client';
import axios, { AxiosResponse } from 'axios';
import HttpStatus from 'http-status-codes';

import {
    getFileSize,
    getTokenPromise,
    removeSpecialCharacters,
} from '@trqk/utils';

import { taskUploadToS3 } from './taskUploadToS3';

interface TaskUploadDetectionsToTrqkGapArgs {
    /**
     * The name of the S3 bucket
     */
    bucketName: string;
    /**
     * Region of the S3 bucket
     */
    bucketRegion: string;
    /**
     * Detection file to upload (usually a CSV)
     */
    file: File | S3UploadFile;
    /**
     * ID of a {@link TrqkGap} that will be updated with the detection file asset URL after it is
     * uploaded to S3
     */
    gapAnalysisId: string;
    /**
     * AWS Cognito identity pool ID for the user
     */
    identityPoolId: string;
    /**
     * An ID for the relevant task machine instance
     */
    taskId: string;
    /**
     * URL for the TrqkDirectory GraphQL endpoint
     */
    trqkDirectoryUrl: string;
}

interface TaskUploadDetectionsToTrqkGapContext {
    bindManagedUpload?: BindManagedUpload;
    generateS3Key?: GenerateS3Key;
    getToken: GetToken;
    sendTaskEvent: SendTaskEvent;
}

/**
 * Uploads a detection file (usually a CSV) and uses it to add a detection batch a trqkGap. This is
 * done in two steps:
 * 1. Upload detection file to S3
 * 2. Use the S3 URL to call the `addGapDetectionBatchToTrqkGap` mutation
 * @param obj
 * @param args {TaskUploadDetectionsToTrqkGapArgs}
 * @param context {TaskUploadDetectionsToTrqkGapContext}
 */
export const taskUploadDetectionsToTrqkGap: Resolver = async (
    obj: Record<string, unknown>,
    args: TaskUploadDetectionsToTrqkGapArgs,
    context: TaskUploadDetectionsToTrqkGapContext,
): Promise<UploadDetectionsOutput> => {
    const {
        bucketName,
        bucketRegion,
        file,
        gapAnalysisId,
        identityPoolId,
        taskId,
        trqkDirectoryUrl,
    } = args;
    const id = taskId;
    const filename = removeSpecialCharacters(file.name);
    const filesize = getFileSize({
        bytes: file.size,
        unit: 'MB',
    });
    const { bindManagedUpload, getToken, generateS3Key, sendTaskEvent } =
        context;

    // Update context with current status, filename and filesize
    sendTaskEvent({
        type: 'STATUS',
        value: {
            taskId,
            status: 'UPLOADING',
            output: {
                filename,
                filesize,
                statusMessage: `Uploading ${filesize}MB file`,
            },
        },
    });

    const uploadedAt = Date.now();

    // 1. Upload the detection file to S3

    let uploadResult;

    try {
        uploadResult = await taskUploadToS3(
            obj,
            {
                bindManagedUpload,
                bucketName,
                bucketRegion,
                file,
                doTaskUpdates: {
                    error: true,
                    progress: true,
                    success: false,
                },
                identityPoolId,
                taskId,
            },
            {
                bindManagedUpload,
                generateS3Key,
                sendTaskEvent,
            },
        );
    } catch (error) {
        const { message } = error as Error;

        return {
            id,
            assetUrl: null,
            canceled: false,
            error: message,
            filename,
            filesize,
            statusMessage: 'Detection upload failed',
            success: false,
            trqkGap: null,
            uploadedAt,
        };
    }

    if (uploadResult.aborted) {
        return {
            id,
            assetUrl: null,
            canceled: true,
            error: null,
            filename,
            filesize,
            statusMessage: 'Detection upload canceled',
            success: false,
            trqkGap: null,
            uploadedAt,
        };
    }

    if (!uploadResult.completed || uploadResult.location === null) {
        return {
            id,
            assetUrl: null,
            canceled: false,
            error: 'S3 upload incomplete or location not returned',
            filename,
            filesize,
            statusMessage: 'Detection upload incomplete',
            success: false,
            trqkGap: null,
            uploadedAt,
        };
    }

    const assetUrl = uploadResult.location;

    // 2. Add the detection batch to the trqkGap

    const token = await getTokenPromise(getToken);

    let response: Maybe<
        AxiosResponse<{
            data: { addGapDetectionBatchToTrqkGap: TrqkGap };
            errors: Error[];
        }>
    > = null;

    try {
        response = await axios({
            method: 'post',
            url: trqkDirectoryUrl,
            headers: {
                Authorization: token,
            },
            // throw an error for non-200 status
            validateStatus: (status: number): boolean =>
                status === HttpStatus.OK,
            data: {
                query: `
                    mutation addGapDetectionBatchToTrqkGap(
                        $gap_analysis_id: String!,
                        $asset_url: String!,
                        $filename: String,
                    ) {
                        addGapDetectionBatchToTrqkGap(gap_analysis_id: $gap_analysis_id, asset_url: $asset_url, filename: $filename) {
                            date_created
                            description
                            detection_batch {
                                audio_count
                                audio_count_raw
                                audios_detected_raw
                                channel_count
                                channel_count_raw
                                detection_count
                                detection_count_raw
                                detection_range_end
                                detection_range_raw_end
                                detection_range_raw_start
                                detection_range_start
                                detection_source
                                filename
                            }
                            entity_id
                            gap_analysis_id
                            performance_period
                            performance_period_end
                            performance_period_start
                            statement_id_international
                            statement_id_standard
                            user_id
                        }
                    }
                `,
                variables: {
                    gap_analysis_id: gapAnalysisId,
                    asset_url: assetUrl,
                    filename,
                },
            },
        });

        // Data in the response is not actually returned from this task; we just check to make sure
        // there was not a non-200 status or any errors in the result.

        if (response?.data?.errors) {
            throw new Error(
                `Error(s) in GraphQL mutation: \n${JSON.stringify(
                    response.data.errors,
                    null,
                    4,
                )}`,
            );
        }
    } catch (error) {
        const { message } = error as Error;

        sendTaskEvent({
            type: 'ERROR',
            value: {
                taskId,
                error: message,
            },
        });

        return {
            id,
            assetUrl,
            canceled: false,
            error: message,
            filename,
            filesize,
            statusMessage: 'Detection batch creation failed',
            success: false,
            trqkGap: null,
            uploadedAt,
        };
    }

    const trqkGap = response?.data?.data?.addGapDetectionBatchToTrqkGap ?? null;

    sendTaskEvent({
        type: 'SUCCESS',
        value: {
            taskId,
            output: {
                assetUrl,
                filename,
                trqkGap,
            },
        },
    });

    return {
        id,
        assetUrl,
        canceled: false,
        error: null,
        filename,
        filesize,
        statusMessage: 'Complete',
        success: true,
        trqkGap,
        uploadedAt,
    };
};

export const TASK_UPLOAD_DETECTIONS_TO_TRQK_GAP = gql`
    mutation TaskUploadDetectionsToTrqkGap(
        $bucketName: String!
        $bucketRegion: String!
        $file: Upload!
        $gapAnalysisId: ID!
        $identityPoolId: String!
        $taskId: ID!
        $trqkDirectoryUrl: String!
    ) {
        taskUploadDetectionsToTrqkGap(
            bucketName: $bucketName
            bucketRegion: $bucketRegion
            file: $file
            gapAnalysisId: $gapAnalysisId
            identityPoolId: $identityPoolId
            taskId: $taskId
            trqkDirectoryUrl: $trqkDirectoryUrl
        ) @client
    }
`;
